// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// When each preference was last chosen (07 §7).
//
// Last-write-wins needs a time per preference, and this is where the times live. Split from
// `client/preferences.ts` — which owns *which* preferences sync and what a valid value is — for
// one structural reason: **the modules that write preferences have to stamp them, and that module
// imports those same modules to build its registry.** One file would be a cycle whose failure mode
// is a `const` read during another module's initialisation, which is a `undefined` in a lookup
// table rather than an error anybody sees.
//
// So this is a leaf. It imports `local.ts` and nothing else, and every preference writer in the
// product can reach it without importing anything that reaches back.
//
// **Every write stamps, including a write back to the default.** That is the case an unstamped
// implementation gets wrong and cannot detect: the client stores a default by *removing* the row,
// so without a stamp the account still holds the old value, nothing records that the player
// changed their mind, and the next sign-in silently undoes it. The argument in full is at the top
// of `server/ts/preferences.ts`.

import { readLocal, safeStorage, writeLocal, type StorageLike } from './local'

/** One key for all of them, because they are only ever read together. */
export const STAMPS_KEY = 'hcc.pinball.pref-stamps'

/**
 * Write down that a preference just changed.
 *
 * The clock is the caller's to supply only in tests; everywhere else this is the wall clock, which
 * is exactly the thing the simulation core is forbidden to touch and this is not. A preference is
 * a fact about a person and a moment, not about a step index.
 */
export function stampPreference(
  name: string,
  storage: StorageLike | undefined = safeStorage(),
  now: Date = new Date(),
): void {
  const stamps = readStamps(storage)
  stamps[name] = now.toISOString()
  writeLocal(storage, STAMPS_KEY, JSON.stringify(stamps))
}

/** Record several at once, for a sync applying a batch. One write rather than one per name. */
export function stampPreferences(
  stamped: Readonly<Record<string, string>>,
  storage: StorageLike | undefined = safeStorage(),
): void {
  writeLocal(storage, STAMPS_KEY, JSON.stringify({ ...readStamps(storage), ...stamped }))
}

/** The stamp map, or an empty one. A corrupt map is discarded rather than thrown over. */
export function readStamps(
  storage: StorageLike | undefined = safeStorage(),
): Record<string, string> {
  const raw = readLocal(storage, STAMPS_KEY)
  if (raw === undefined) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {}
    const out: Record<string, string> = {}
    for (const [name, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === 'string') out[name] = at
    }
    return out
  } catch {
    // `local.ts`'s rule: a preference that cannot be read is a small annoyance, and a page that
    // will not render because of one is not.
    return {}
  }
}

// **There is no `forgetStamps`, and its absence is a decision rather than an omission.** Two
// plausible callers were considered and neither survives reading.
//
// Settings' "forget what this browser is kept" clears scores and replays. Clearing the stamps
// there would leave the *values* in place looking never-chosen, so the next sync would quietly
// replace them from the account — a button that says it forgets things silently restoring
// different ones.
//
// Signing out is the other, and it is worse: somebody signing into a second account on a shared
// laptop would push the first account's theme into the second, or lose their own, depending on
// which way it was written. That is a real question about what a device's settings *are* when two
// people share it, and it deserves an answer rather than a side effect of a helper that existed.
// Recorded as open in the M3-54 entry.
